<template>
  <div :class="[baseClass, { [`border border-radius-6 flex`]: !isEmpty }]">
    <template v-if="!isEmpty">
      <div :class="[`${baseClass}__preview`, { 'short fit': shortMode }]">
        <div
          :class="{ 'relative-position overflow-hidden fit border border-radius-6 flex items-center justify-center cursor-pointer': shortMode }"
          @mouseenter="showOverlay = true"
          @mouseleave="showOverlay = false"
          @click="onChooseImage"
        >
          <q-img
            v-if="image"
            :src="image"
            :width="shortMode ? '100%' : '31px'"
            :height="shortMode ? '100%' : '31px'"
            contain
          />
          <q-img
            v-else
            :src="defaultImageUrl"
            width="31px"
          />
          <transition
            enter-active-class="animated fadeIn"
            leave-active-class="animated fadeOut"
          >
            <div
              v-if="shortMode && showOverlay && image"
              class="absolute fit flex items-center justify-center overlay"
              @click.stop="onRemove"
            >
              <q-icon
                color="white"
                name="fl:trash"
                size="19px"
              />
            </div>
          </transition>
        </div>
      </div>
      <div
        v-if="!shortMode"
        :class="[`${baseClass}__input-block`, 'flex items-center justify-center col']"
      >
        <label :for="id">
          {{ $t(canRemove ? 'button.changeImage' : 'button.loadImage') }}
        </label>
        <q-icon
          v-if="canRemove"
          name="fa fa-trash-can"
          class="icon-remove"
          size="15px"
          color="grey"
          @click="onRemove"
        />
      </div>
    </template>
    <q-input
      :id="id"
      ref="file"
      v-bind="$attrs"
      :value="image"
      :accept="accept"
      type="file"
      @change="onChange"
    />
  </div>
</template>

<script>
import defaultImageHost from 'common/helpers/defaultImageHost';
import { dataUrlToFile, getBase64 } from 'common/helpers/file';
import randomString from 'common/helpers/randomString';

export default {
  name: 'AppImagePicker',
  props: {
    value: {
      type: String,
      default: undefined,
    },
    accept: {
      type: String,
      default: 'image/png,image/jpeg,image/gif',
    },
    shortMode: {
      type: Boolean,
      default: false,
    },
    defaultImageUrl: {
      type: String,
      default: defaultImageHost,
    },
    isEmpty: {
      type: Boolean,
      default: false,
    },
    emptyValue: {
      type: String,
      default: '',
    },
  },
  emits: ['input'],
  data() {
    return {
      baseClass: 'app-image-picker',
      image: undefined,
      defaultImageBase64: undefined,
      showOverlay: false,
      id: undefined,
    };
  },
  computed: {
    canRemove() {
      return this.image
        && this.image !== this.defaultImageBase64
        && this.image !== this.defaultImageUrl;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.image = value;
      },
    },
  },
  async created() {
    this.id = randomString();
    this.defaultImageBase64 = await dataUrlToFile(this.defaultImageUrl, 'image');
    this.defaultImageBase64 = await getBase64(this.defaultImageBase64);
  },
  methods: {
    async onChange(event) {
      const { files } = event.target;
      if (files.length === 0) {
        return;
      }
      this.image = await getBase64(files[0]);
      this.$emit('input', this.image);
    },
    onRemove() {
      this.image = this.emptyValue;
      this.$emit('input', this.image);
    },
    onChooseImage() {
      if (!this.shortMode && !this.isEmpty) {
        return;
      }
      this.$refs.file.$refs.input.click();
    },
  },
};
</script>

<style scoped lang="scss">
.app-image-picker {
  ::v-deep .q-field__control {
    display: none;
  }

  &__preview {
    &.short {
      padding: 15px;
    }
    &:not(.short) {
      border-right: 1px solid $grey-2;
      padding: 10px 15px;
    }
  }

  &__input-block {
    gap: 15px;

    label {
      cursor: pointer;
      font-size: 14px;
      font-weight: 400;
    }

    .icon-remove {
      cursor: pointer;
    }
  }

  .overlay {
    background: rgb(0 0 0 / 30%);
  }
}
</style>
